// @flow
/** @jsx jsx */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Query } from 'react-apollo';
import { jsx } from '@emotion/core';
import cx from 'classnames';

import GridApi from '../../components/GridApi';
import PageContainer from '../../components/PageContainer';
import ActionsHeader from '../../components/ActionsHeader';
import ModuleQuery from '../../components/ModuleQuery';
import Curtain from '../../components/Curtain';
import Row from '../../components/Row';
import Col from '../../components/Col';
import Notifications from '../../components/Notifications';
import ContentChecker from '../../components/ContentChecker';
import BlockingStatus from '../../components/BlockingStatus';
import GridApiController from '../../controllers/GridApiController';
import RefetchController from '../../controllers/RefetchController';
import userSettingsQuery from '../../graphql/queries/userSettingsInventory.query';
import moduleDataQuery from '../../graphql/queries/inventoryData.query';
import getActiveOrderQuery from '../../graphql/queries/getActiveOrder.query';
import formatters from './formatters';
import ActionsOrganizer from './components/ActionsOrganizer';
import {
  ActionBarStateProvider,
  ActionBarStateConsumer,
} from '../../context/actionBarState.context';
import { OrderContentConsumer } from '../../context/orderContent.context';
import { getDefaultUserSettings } from '../../utils'
import Loading from '../../components/Loading'
import { MobileView, useMobileMediaQuery } from '../../config/mediaQuery';
import ModuleMenuBar from '../../components/ModuleMenuBar';
import StateSelector from '../../components/actions/StateSelectorAction';
import MonthSelector from '../../components/actions/MonthSelectorAction';
import useMenuHandler from '../../hooks/useMenuHandler';

type Props = {
  isFullPage: boolean,
  showGroupPanel?: boolean,
  paginated?: boolean,
  active: boolean,
};

/**
 * Effect component that handles client-side price and visibility filtering.
 * 
 * This component is responsible for:
 * 1. Applying visibility filter on INITIAL LOAD to hide items without prices
 *    for the selected state (server sends ALL items for ANY state)
 * 2. Updating prices and visibility when month/state changes
 * 3. Reapplying prices after data refetch (Refresh button)
 * 
 * Key behaviors:
 * - Runs on initial load to apply visibility filter (server sends all items)
 * - Runs on subsequent month/state changes to update prices and visibility
 * - Runs after refetch completes to reapply current state's pricing
 * - Uses the updatePriceFields method from GridApiController
 */
const PriceUpdateEffect = ({
  month,
  state,
  updatePriceFields,
  collection,
  refetched,
}: {
  month: string,
  state: string,
  updatePriceFields: (string, string, boolean) => number,
  collection: any,
  refetched: boolean,
}) => {
  // Track the previous values to detect actual changes
  const prevMonth = useRef(month);
  const prevState = useRef(state);
  // Track if we've applied the initial filter
  const hasAppliedInitialFilter = useRef(false);
  // Track refetched to detect when refetch completes
  const prevRefetched = useRef(refetched);
  
  useEffect(() => {
    // Wait for collection to be available
    if (!collection) {
      return;
    }
    
    // Check if month or state actually changed
    const monthChanged = prevMonth.current !== month;
    const stateChanged = prevState.current !== state;
    const isInitialFilter = !hasAppliedInitialFilter.current;
    // Detect refetch completion (refetched transitions from false to true)
    const justRefetched = refetched && !prevRefetched.current;
    
    // Apply filter on initial load, month/state change, OR after refetch completes
    if (isInitialFilter || monthChanged || stateChanged || justRefetched) {
      const reason = isInitialFilter ? 'Initial filter' : 
                     justRefetched ? 'Post-refetch' : 
                     'Month/State changed';
      console.log(`[PriceUpdateEffect] ${reason}: month=${month}, state=${state}`);
      
      // Update prices and apply visibility filter
      // Pass true for forceFilter on initial load or post-refetch to ensure filter is applied
      const updatedCount = updatePriceFields(month, state, isInitialFilter || justRefetched);
      console.log(`[PriceUpdateEffect] Updated ${updatedCount} items, filter applied`);
      
      // Update refs for next comparison
      prevMonth.current = month;
      prevState.current = state;
      hasAppliedInitialFilter.current = true;
    }
    
    // Always update refetched ref to track state transitions
    prevRefetched.current = refetched;
  }, [month, state, updatePriceFields, collection, refetched]);
  
  // This component doesn't render anything
  return null;
};

const App = (props: Props) => {
  const moduleName = 'inventory';

  const menuRef = useRef();
  const menuButtonRef = useRef();

  const {
    isFullPage = true,
    showGroupPanel = true,
    active
  } = props;

  const isMobile = useMobileMediaQuery();
  const [displayMenu, setDisplayMenu] = useState(false);
  const [handlerAdded, setHandlerAdded] = useState(false);
  
  // State to track the displayed view name for UI updates
  // - undefined: initial state, will sync from query on first load
  // - null: user selected "Default View"
  // - string: user selected a named view
  const [displayedViewName, setDisplayedViewName] = useState(undefined);

  const changeDisplayMenu = (status) => {
    setDisplayMenu(status);
  }

  const handler = useCallback((e) => {
    if (menuRef.current
      && menuButtonRef.current
      && !menuRef.current.contains(e.target)
      && !menuButtonRef.current.contains(e.target)
    ) {
      setDisplayMenu(value => {
        if (value) {
          return false;
        }
        return value;
      });
    }
  }, []);

  useMenuHandler(handler, active, setHandlerAdded);

  return (
    <React.Fragment>
      <Query
        query={userSettingsQuery}
        variables={{
          moduleName: moduleName,
        }}
        fetchPolicy="network-only"
      >
        {({ data: dataGrid, loading, error }: any) => {
          if (!dataGrid || loading) {
            return (
              <BlockingStatus
                excludeHeader
                active
                container={document.body}
                text="Loading..."
              />
            );
          }

          // Get view name from query - null means Default View
          var queryViewName = dataGrid && dataGrid.userSettings && dataGrid.userSettings.viewName
            ? dataGrid.userSettings.viewName
            : null;

          // Build userSettings based on whether we have a named view or Default View
          // IMPORTANT: When viewName is null (Default View), we should NOT pass
          // the server-returned columns/sorting/groups/frozenColumns/filterDefinition
          // because those are empty/stale for Default View. Instead, we let
          // GridApiController fall back to the default settings.
          // 
          // This fixes the bug where loading Default View on initial page refresh
          // would not apply default settings correctly, while switching from a
          // named view back to Default View would work (because onViewReset is
          // explicitly called with default values).
          var userSettings;
          if (queryViewName) {
            // Named view - use server-returned settings
            userSettings = dataGrid && dataGrid.userSettings
              ? {
                columnsMapping: dataGrid.userSettings.columns,
                frozenColumns: dataGrid.userSettings.frozenColumns,
                groups: dataGrid.userSettings.groups,
                sorting: dataGrid.userSettings.sorting,
                state: dataGrid.userSettings.state,
                states: dataGrid.userSettings.states,
                month: dataGrid.userSettings.month,
                months: dataGrid.userSettings.months,
                filterDefinition: dataGrid.userSettings.filterDefinition,
              }
              : {};
          } else {
            // Default View - only pass non-view-specific settings
            // Let GridApiController use defaultColumns, defaultSorting, defaultGroupPanels
            userSettings = dataGrid && dataGrid.userSettings
              ? {
                state: dataGrid.userSettings.state,
                states: dataGrid.userSettings.states,
                month: dataGrid.userSettings.month,
                months: dataGrid.userSettings.months,
                // Note: columnsMapping, frozenColumns, groups, sorting, filterDefinition
                // are intentionally omitted so defaults are applied
              }
              : {};
          }

          const defaultUserSettings = getDefaultUserSettings(dataGrid)
          
          // Only sync displayedViewName from query on initial load (when displayedViewName is undefined)
          // After initial load, displayedViewName is managed entirely by user selection
          if (displayedViewName === undefined) {
            // Use setTimeout to avoid setState during render
            setTimeout(function() { setDisplayedViewName(queryViewName); }, 0);
          }
          
          // Determine what to show in the UI:
          // - If displayedViewName is undefined (initial load), use queryViewName
          // - Otherwise use displayedViewName (which could be null for Default View)
          var currentViewNameForUI = displayedViewName === undefined ? queryViewName : displayedViewName;

          // Handler for when a view is loaded - just updates the UI display name
          // The actual view settings are applied by onApplyViewSettings in ActionsHeader
          var handleViewLoaded = function(viewName) {
            setDisplayedViewName(viewName);
          };

          return (
            <ActionBarStateProvider
              territorialStates={userSettings.states}
              months={userSettings.months}
              selectedState={userSettings.state}
              selectedMonth={userSettings.month}
            >
              <PageContainer
                className={cx({
                  'page--is-full-page': isFullPage,
                  'page--is-nested': !isFullPage,
                  'page--with-group-panel': showGroupPanel,
                })}
                isFullPage={isFullPage}
                active={props.active}
              >
                <ActionBarStateConsumer>
                  {({
                    territorialState,
                    month,
                    onChangeTerritorialState,
                    onChangeMonth,
                  }: any) => (
                    <React.Fragment>
                      <OrderContentConsumer>
                        {({
                          selectedOrderType,
                          selectedOrderDate,
                          orderItems,
                          onChangeOrderItems,
                          hasLocalOrderItems,
                        }: any) => {
                          return (
                            <ModuleQuery
                              query={moduleDataQuery}
                              variables={{
                                // NOTE: state and month are intentionally NOT passed here.
                                // This prevents Apollo from refetching when these dropdown values change.
                                // Instead, price filtering is handled client-side by PriceUpdateEffect
                                // using the updatePriceFields method from GridApiController.
                                // 
                                // The server uses the user's default state/month from userSettings
                                // for the initial load, and linkedPrices contains all price data
                                // for client-side filtering.
                                //
                                // This optimization reduces month/state switch time from ~67s to ~100ms.
                                selectedOrderType,
                                selectedOrderDate,
                              }}
                            >
                              {({
                                data: moduleData,
                                loading: moduleLoading,
                                refetch: moduleRefetch,
                                refetching: moduleRefetching,
                                refetched: moduleRefetched,
                              }) => {
                                return (
                                  <GridApiController
                                    gridType={moduleName}
                                    data={moduleData.inventoryContent}
                                    loading={loading || moduleLoading}
                                    refetching={moduleRefetching}
                                    refetched={moduleRefetched}
                                    refetch={moduleRefetch}
                                    defaultColumns={defaultUserSettings.defaultColumns}
                                    defaultSorting={defaultUserSettings.defaultSorting}
                                    defaultGroupPanels={
                                      defaultUserSettings.defaultGroupPanels
                                    }
                                    active={props.active}
                                    orderItems={orderItems}
                                    onChangeOrderItems={onChangeOrderItems}
                                    hasLocalOrderItems={hasLocalOrderItems}
                                    globalFilters={`${territorialState}${month}`}
                                    {...userSettings}
                                    selectedState={territorialState}
                                    isMobile={isMobile}
                                  // shouldKeepCollection
                                  >
                                    {({
                                      selectedItems,
                                      gridUpdated,
                                      collection,
                                      columnsMapping,
                                      currentItem,
                                      onGridUpdated,
                                      onGridRef,
                                      onGridWillUnmount,
                                      onColSorted,
                                      onColResized,
                                      onColDragged,
                                      onColFreezed,
                                      onRowFreezed,
                                      onRowSelected,
                                      onColumnsMappingChange,
                                      onCurrentItemChanged,
                                      onGroupPanelChanged,
                                      onClearSelection,
                                      onItemAdded,
                                      onItemUpdated,
                                      itemAddedStatus,
                                      saveStatus,
                                      onViewSaved,
                                      onViewReset,
                                      onApplyViewSettings,
                                      grid,
                                      userViewState,
                                      refetching,
                                      hasAllColumnsHidden,
                                      frozenColumns,
                                      refetch,
                                      isLoading,
                                      isRefetching,
                                      isCollapseLoading,
                                      setCollapseLoading,
                                      hasDataToDisplay,
                                      playResetAnimation,
                                      handleResetAnimation,
                                      setFlexGridFilter,
                                      getGridFilterDefinition,
                                      clearInternalFilters,
                                      toggleMultiSort,
                                      multiColumnSort,
                                      updatePriceFields,
                                    }) => {
                                      return (
                                        <React.Fragment>
                                          {/* Client-side price and visibility filtering effect */}
                                          {/* This handles initial load, month/state changes, AND post-refetch */}
                                          <PriceUpdateEffect
                                            month={month}
                                            state={territorialState}
                                            updatePriceFields={updatePriceFields}
                                            collection={collection}
                                            refetched={moduleRefetched}
                                          />
                                          {/* Show loading indicator during collapse/expand operations */}
                                          {isCollapseLoading && (
                                            <BlockingStatus
                                              excludeHeader
                                              active
                                              container={document.body}
                                              text="Processing..."
                                            />
                                          )}
                                          <Notifications
                                            itemAddedStatus={itemAddedStatus}
                                            saveStatus={saveStatus}
                                          />
                                          <ActionsHeader
                                            grid={grid}
                                            collection={collection}
                                            defaultColumnsMapping={
                                              defaultUserSettings.defaultColumns
                                            }
                                            columnsMapping={columnsMapping}
                                            selectedItems={selectedItems}
                                            moduleName={moduleName}
                                            saveStatus={saveStatus}
                                            loading={
                                              isLoading || isRefetching
                                            }
                                            onViewSaved={onViewSaved}
                                            onViewReset={onViewReset}
                                            onApplyViewSettings={onApplyViewSettings}
                                            onColumnsMappingChange={
                                              onColumnsMappingChange
                                            }
                                            userViewState={userViewState}
                                            refetch={refetch}
                                            hasAllColumnsHidden={
                                              hasAllColumnsHidden
                                            }
                                            hasDataToDisplay={
                                              hasDataToDisplay
                                            }
                                            frozenColumns={frozenColumns}
                                            currentItem={currentItem}
                                            state={territorialState}
                                            month={month}
                                            onItemAdded={onItemAdded}
                                            itemAddedStatus={itemAddedStatus}
                                            useLegacyColumnsManager={true}
                                            defaultSettings={defaultUserSettings}
                                            handleResetAnimation={handleResetAnimation}
                                            toggleMultiSort={toggleMultiSort}
                                            multiColumnSort={multiColumnSort}
                                            // Multiple saved views feature props
                                            useMultipleViews={true}
                                            currentViewName={currentViewNameForUI}
                                            onViewLoaded={handleViewLoaded}
                                            getGridFilterDefinition={getGridFilterDefinition}
                                            // Skip server refetch when state/month changes - price filtering
                                            // is handled entirely client-side by PriceUpdateEffect
                                            skipStateMonthRefetch={true}
                                          >
                                            {actions => {
                                              const { ColumnsAction, isOneRowSelected, loading, hasDataToDisplay } = actions;
                                              return (
                                                <>
                                                  <MobileView>
                                                    <ModuleMenuBar
                                                      moduleRefetch={moduleRefetch}
                                                      changeDisplayMenu={changeDisplayMenu}
                                                      ColumnsAction={ColumnsAction}
                                                      displayMenu={displayMenu}
                                                      stateSelector={<StateSelector disabled={!hasDataToDisplay || loading} />}
                                                      monthSelector={<MonthSelector disabled={!hasDataToDisplay || loading} />}
                                                      menuButtonRef={menuButtonRef}
                                                      clearInternalFilters={clearInternalFilters}
                                                    />
                                                  </MobileView>
                                                  <div ref={menuRef}>
                                                    <Row
                                                      css={{
                                                        justifyContent:
                                                          'space-between',
                                                        padding: '0 10px',
                                                        ...(isMobile && {
                                                          position: 'absolute',
                                                          bottom: 0,
                                                          boxShadow:
                                                            '0px -4px 4px 0px #ACACAC40',
                                                          zIndex: 100,
                                                          background: '#fff',
                                                          borderRadius: '8px 8px 0px 0px',
                                                          width: '100%',
                                                          display: displayMenu || isOneRowSelected
                                                            ? 'flex'
                                                            : 'none',
                                                          justifyContent: 'center',
                                                          height: (displayMenu && '189px') || (isOneRowSelected && !currentItem.Restrictions ? '210px': '230px'),
                                                        }),
                                                      }}
                                                    >
                                                      <Col>
                                                        <ActionsOrganizer
                                                          onItemUpdated={
                                                            onItemUpdated
                                                          }
                                                          minHeight="80px"
                                                          selectedOrderType={
                                                            selectedOrderType
                                                          }
                                                          selectedOrderDate={
                                                            selectedOrderDate
                                                          }
                                                          moduleRefetch={moduleRefetch}
                                                          clearInternalFilters={clearInternalFilters}
                                                          {...actions}
                                                          displayRowMenu={isOneRowSelected}
                                                          displayMenu={displayMenu}
                                                          toggleMultiSort={toggleMultiSort}
                                                        />
                                                      </Col>
                                                    </Row>
                                                  </div>
                                                </>
                                              )
                                            }}
                                          </ActionsHeader>
                                          <ContentChecker
                                            isLoading={isLoading}
                                            isRefetching={isRefetching}
                                            hasAllColumnsHidden={
                                              hasAllColumnsHidden
                                            }
                                            hasDataToDisplay={
                                              hasDataToDisplay
                                            }
                                            collection={collection}
                                          >
                                            {() => {
                                              return (
                                                <React.Fragment>
                                                  <Query
                                                    query={
                                                      getActiveOrderQuery
                                                    }
                                                  >
                                                    {({
                                                      data: activeOrderData,
                                                      loading: activeOrderLoading,
                                                    }) => {
                                                      if (activeOrderLoading) {
                                                        return <Loading />
                                                      }

                                                      const activeOrderSrc =
                                                        activeOrderData &&
                                                        activeOrderData.activeOrder &&
                                                        activeOrderData.activeOrder;

                                                      const hasActiveOrder =
                                                        activeOrderSrc &&
                                                        activeOrderSrc.Account !==
                                                        null;

                                                      return (
                                                        <RefetchController
                                                          content={JSON.stringify(
                                                            activeOrderSrc.Det,
                                                          )}
                                                          condition={[
                                                            !!hasActiveOrder,
                                                            selectedOrderType,
                                                            (
                                                              options,
                                                              prevOptions,
                                                            ) => {
                                                              if (
                                                                !props
                                                                  .active &&
                                                                JSON.stringify(
                                                                  options.content,
                                                                ) !==
                                                                JSON.stringify(
                                                                  prevOptions.content,
                                                                )
                                                              ) {
                                                                return true;
                                                              }

                                                              return false;
                                                            },
                                                          ]}
                                                          refetch={
                                                            moduleRefetch
                                                          }
                                                          level={
                                                            hasActiveOrder &&
                                                            activeOrderSrc
                                                              .Account.Level
                                                          }
                                                        />
                                                      );
                                                    }}
                                                  </Query>
                                                  <Curtain
                                                    container={
                                                      grid && grid.hostElement
                                                    }
                                                    active={props.active}
                                                    startWhen={
                                                      props.active &&
                                                      !isLoading &&
                                                      !isRefetching
                                                    }
                                                    text={
                                                      isRefetching
                                                        ? 'Updating grid'
                                                        : undefined
                                                    }
                                                  />
                                                  <GridApi
                                                    collection={collection}
                                                    columnsMapping={
                                                      columnsMapping
                                                    }
                                                    frozenColumns={
                                                      frozenColumns
                                                    }
                                                    onRowSelected={
                                                      onRowSelected
                                                    }
                                                    onColSorted={onColSorted}
                                                    onColResized={
                                                      onColResized
                                                    }
                                                    onColDragged={
                                                      onColDragged
                                                    }
                                                    onColFreezed={
                                                      onColFreezed
                                                    }
                                                    onRowFreezed={
                                                      onRowFreezed
                                                    }
                                                    onGridUpdated={
                                                      onGridUpdated
                                                    }
                                                    onGridWillUnmount={
                                                      onGridWillUnmount
                                                    }
                                                    onCurrentItemChanged={
                                                      onCurrentItemChanged
                                                    }
                                                    onGroupPanelChanged={
                                                      onGroupPanelChanged
                                                    }
                                                    onClearSelection={
                                                      onClearSelection
                                                    }
                                                    ref={onGridRef}
                                                    refetching={isRefetching}
                                                    hasAllColumnsHidden={
                                                      hasAllColumnsHidden
                                                    }
                                                    hasDataToDisplay={
                                                      hasDataToDisplay
                                                    }
                                                    formatters={formatters}
                                                    formattersArgs={{
                                                      selectedState: territorialState,
                                                    }}
                                                    state={territorialState}
                                                    showFrozenBars
                                                    showGroupPanel={showGroupPanel}
                                                    active={props.active}
                                                    playResetAnimation={playResetAnimation}
                                                    setFlexGridFilter={setFlexGridFilter}
                                                    menuRef={menuRef}
                                                    isMobile={isMobile}
                                                    isFullPage={isFullPage}
                                                    handlerAdded={handlerAdded}
                                                    multiColumnSort={multiColumnSort}
                                                  />
                                                </React.Fragment>
                                              );
                                            }}
                                          </ContentChecker>
                                        </React.Fragment>
                                      );
                                    }}
                                  </GridApiController>
                                );
                              }}
                            </ModuleQuery>
                          );
                        }}
                      </OrderContentConsumer>
                    </React.Fragment>
                  )}
                </ActionBarStateConsumer>
              </PageContainer>
            </ActionBarStateProvider>
          );
        }}
      </Query>
    </React.Fragment>
  );
};

export default App;
